import { useLayoutEffect, useRef } from 'react';
import { TAB_SWAP_SESSION_STORAGE_KEY } from 'consts';
import { absoluteOffsetTop } from 'utils/absoluteOffsetTop';

/** Read + clear the one-shot tab-swap anchor (the tab bar's viewport-top at swap time). */
const consumeSwapAnchor = (): number | null => {
  try {
    const raw = sessionStorage.getItem(TAB_SWAP_SESSION_STORAGE_KEY);
    if (raw != null) sessionStorage.removeItem(TAB_SWAP_SESSION_STORAGE_KEY);
    const value = raw == null ? NaN : Number(raw);
    return Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
};

/**
 * Keeps a tab bar at the same viewport position across a tab swap. The view being left calls
 * `captureSwapAnchor` before navigating; the view arriving re-anchors itself on mount so the
 * sticky tab bar does not jump. The target is clamped to [0, the bar's document offset]: at most
 * "tabs at the top, content below", never above the top. The destination owns scroll on a swap:
 * ScrollToTop sits the navigation out while the anchor is present, so one scroll before paint is
 * enough. The offset comes off the offsetParent chain rather than a client rect because a sticky
 * bar usually arrives pinned, where a rect reports the pinned offset instead of the real one.
 */
const useTabSwapAnchor = <T extends HTMLElement>() => {
  const tabsRef = useRef<T | null>(null);

  useLayoutEffect(() => {
    const anchorTop = consumeSwapAnchor();
    const node = tabsRef.current;
    if (anchorTop == null || !node) return;
    const docTop = absoluteOffsetTop(node);
    window.scrollTo({
      top: Math.min(Math.max(docTop - anchorTop, 0), docTop),
      left: 0,
    });
  }, []);

  /** Capture the tab bar's current viewport-top so the destination view can re-anchor it. */
  const captureSwapAnchor = () => {
    try {
      const top = tabsRef.current?.getBoundingClientRect().top;
      if (top != null) {
        sessionStorage.setItem(TAB_SWAP_SESSION_STORAGE_KEY, String(top));
      }
    } catch {
      // sessionStorage unavailable: the destination falls back to default scroll behaviour.
    }
  };

  return { captureSwapAnchor, tabsRef };
};

export default useTabSwapAnchor;
